import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';
import LlmHealthPill from './LlmHealthPill';

const SECOND_NAV_KEY = 'aitutor.secondNav.open';

// Compute preview-mode chip from current pathname.
//   /preview-as-faculty  → "VIEWING AS FACULTY"
//   /preview-as-student  → "VIEWING AS STUDENT"
//   otherwise            → null
const previewChipFromPath = (pathname = '') => {
  if (pathname.startsWith('/preview-as-faculty')) return 'VIEWING AS FACULTY';
  if (pathname.startsWith('/preview-as-student')) return 'VIEWING AS STUDENT';
  return null;
};

/**
 * IoA Navbar — v3 brand lockup
 * Primary bar (light bone): owl + INSTITUTE OF ANALYTICS (USA) wordmark
 *   with a dark navy ribbon below holding the white "BIG DATA INTELLIGENCE
 *   TRAINING" tagline.
 * Secondary bar (light bone): both U.S. Provisional Patent Application
 *   numbers shown in full, plus a reserved slot for future notations
 *   (accreditations, compliance badges, course IDs).
 */
const Navbar = () => {
  const { user, logout } = useAuth();
  const brand = useBrand();
  const location = useLocation();
  const previewLabel = previewChipFromPath(location.pathname);

  // Second nav bar (patents + this-week-message) is collapsed by default.
  // Toggle persists in localStorage so the choice sticks across pages.
  const [secondOpen, setSecondOpen] = useState(() => {
    try { return localStorage.getItem(SECOND_NAV_KEY) === '1'; }
    catch { return false; }
  });
  const toggleSecond = () => {
    const next = !secondOpen;
    setSecondOpen(next);
    try { localStorage.setItem(SECOND_NAV_KEY, next ? '1' : '0'); } catch { /* noop */ }
  };

  return (
    <header className="sticky top-0 z-30" data-testid="app-navbar">
      {/* ───────── Primary bar ───────── */}
      <div
        className="border-b"
        style={{ background: 'var(--gr-bone)', borderColor: 'var(--gr-border)' }}
      >
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-5">
          {/* Brand: owl + wordmark + navy tagline ribbon */}
          <Link
            to={user ? `/${user.role}` : '/login'}
            className="flex items-center gap-4 shrink-0"
            data-testid="hero-aitutor-logo"
          >
            <img
              src="/branding/ioa-owl-only.png?v=2"
              alt="IoA Owl"
              className="h-[72px] w-auto object-contain"
              data-testid="navbar-owl"
            />
            <div className="leading-none" data-testid="hero-ioa-wordmark">
              <div
                className="whitespace-nowrap"
                style={{
                  fontFamily: '"Anton", "Oswald", "Impact", sans-serif',
                  fontSize: '34px',
                  letterSpacing: '0.01em',
                  color: 'var(--ioa-blue, #1E90FF)',
                  textTransform: 'uppercase',
                  lineHeight: 1,
                }}
              >
                INSTITUTE OF ANALYTICS{' '}
                <span style={{ color: 'var(--ioa-red, #E5202B)' }}>(USA)</span>
                <span
                  style={{
                    color: 'var(--gr-navy)',
                    fontSize: '0.34em',
                    verticalAlign: 'super',
                    fontFamily: '"Oswald", sans-serif',
                    marginLeft: '4px',
                  }}
                >
                  ™
                </span>
              </div>
              <div
                className="mt-2 text-center"
                style={{
                  background: 'var(--gr-navy)',
                  border: '1px solid var(--gr-navy-dark)',
                  color: '#FFFFFF',
                  fontFamily: '"Oswald", sans-serif',
                  fontWeight: 700,
                  letterSpacing: '0.22em',
                  fontSize: '12.5px',
                  textTransform: 'uppercase',
                  padding: '5px 12px',
                  borderRadius: '5px',
                  boxShadow: '0 1px 2px rgba(26,43,76,0.2)',
                }}
                data-testid="navbar-tagline-ribbon"
              >
                Big Data Intelligence Training
              </div>
            </div>
          </Link>

          {/* Right: course title + user controls */}
          <div className="flex items-center gap-4 min-w-0">
            <div className="text-right leading-tight min-w-0 hidden lg:block">
              <div className="font-bold text-gray-900 truncate" data-testid="hero-course-title">
                {brand.course_title}
              </div>
              <div className="text-xs text-gray-500 truncate" data-testid="hero-course-subtitle">
                {brand.course_subtitle}
              </div>
            </div>

            {user && (
              <div
                className="flex items-center gap-3 pl-4 border-l"
                style={{ borderColor: 'var(--gr-border)' }}
                data-testid="navbar-user-section"
              >
                {previewLabel && (
                  <span
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-extrabold tracking-wider uppercase animate-pulse"
                    style={{
                      background: '#FEF3C7',          /* amber-100 */
                      color: '#92400E',               /* amber-800 */
                      border: '1px solid #FCD34D',    /* amber-300 */
                    }}
                    data-testid="navbar-preview-chip"
                    title={`You are in preview mode — ${previewLabel.toLowerCase()}.`}
                  >
                    👁 {previewLabel}
                  </span>
                )}
                <LlmHealthPill />
                {user.role === 'developer' && (
                  <a
                    href="/downloads/"
                    data-testid="navbar-downloads-link"
                    className="text-xs px-2.5 py-1.5 rounded-md font-semibold transition-colors hidden sm:inline-flex items-center gap-1 no-underline"
                    style={{
                      background: 'white',
                      color: 'var(--gr-navy)',
                      border: '1px solid #E5E7EB',
                    }}
                    title="Downloads — Patents, whitepapers, sample decks (developer only)">
                    📥 Downloads
                  </a>
                )}
                <div className="hidden md:block" data-testid="navbar-view-switcher">
                  {(() => {
                    // Views available per role
                    const viewsByRole = {
                      developer: [
                        { key: 'developer', label: 'Developer', href: '/developer',            color: '#7C3AED' },
                        { key: 'faculty',   label: 'Faculty',   href: '/preview-as-faculty',   color: '#0891B2' },
                        { key: 'student',   label: 'Student',   href: '/preview-as-student',   color: '#16A34A' },
                      ],
                      teacher: [
                        { key: 'faculty', label: 'Faculty', href: '/teacher',              color: '#0891B2' },
                        { key: 'student', label: 'Student', href: '/preview-as-student',   color: '#16A34A' },
                      ],
                    }[user.role];

                    if (!viewsByRole) {
                      // Student / Executive: no switcher, keep the compact name+role label
                      return (
                        <div className="text-right">
                          <div className="text-[13px] font-semibold" style={{ color: 'var(--gr-navy)' }} data-testid="navbar-user-name">
                            {user.name}
                          </div>
                          <div className="text-[10px] mt-0.5">
                            <span className="badge-role">{user.role}</span>
                          </div>
                        </div>
                      );
                    }

                    const path = window.location.pathname;
                    return (
                      <div style={{
                        display: 'flex', flexDirection: 'column', gap: 2,
                        background: 'rgba(14,42,71,0.04)',
                        border: '1px solid var(--gr-border)',
                        borderRadius: 6, padding: '3px 6px', minWidth: 96,
                      }}>
                        {viewsByRole.map((v) => {
                          const active = path.startsWith(v.href) ||
                            (v.key === 'faculty' && path.startsWith('/teacher')) ||
                            (v.key === 'developer' && path.startsWith('/developer'));
                          return (
                            <Link
                              key={v.key} to={v.href}
                              data-testid={`view-switch-${v.key}`}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                padding: '2px 4px', borderRadius: 3,
                                fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
                                color: active ? v.color : 'var(--gr-navy)',
                                textDecoration: 'none',
                                background: active ? `${v.color}18` : 'transparent',
                              }}
                            >
                              <span style={{
                                width: 6, height: 6, borderRadius: '50%',
                                background: active ? v.color : 'transparent',
                                border: `1px solid ${v.color}`,
                              }} />
                              {v.label}
                            </Link>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
                <button
                  onClick={logout}
                  className="btn-outline text-xs py-1.5 px-3"
                  data-testid="navbar-logout-btn"
                >
                  Log out
                </button>
                {/* Toggle for the second nav bar (patents + week message) */}
                <button
                  onClick={toggleSecond}
                  aria-label={secondOpen ? 'Hide patent strip' : 'Show patent strip'}
                  title={secondOpen ? 'Hide patent + announcements bar' : 'Show patent + announcements bar'}
                  data-testid="navbar-second-toggle"
                  className="rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold transition-colors"
                  style={{
                    background: secondOpen ? 'var(--gr-navy)' : 'transparent',
                    color: secondOpen ? 'var(--gr-gold)' : 'var(--gr-navy)',
                    border: '1.5px solid var(--gr-navy)',
                    lineHeight: 1,
                  }}
                >
                  {secondOpen ? '−' : '+'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ───────── Secondary bar: Patent Pending + this-week marquee ───────── */}
      {secondOpen && (
        <div
          className="border-b"
          style={{ background: 'var(--gr-bone)', borderColor: 'var(--gr-border)' }}
          data-testid="navbar-second-bar"
        >
          <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between gap-6 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap" data-testid="navbar-patent-strip">
              <span
                style={{
                  background: 'var(--gr-navy)',
                  color: 'var(--gr-gold)',
                  fontFamily: '"Oswald", sans-serif',
                  fontWeight: 700,
                  letterSpacing: '0.14em',
                  fontSize: '11px',
                  padding: '4px 10px',
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                }}
              >
                Patent Pending
              </span>
              <div className="flex items-center gap-3 flex-wrap">
                {['64/084,230', '64/084,281', '64/093,353', '64/114,654'].map((num) => (
                  <span
                    key={num}
                    style={{
                      background: 'rgba(252,211,89,0.18)',
                      border: '1px solid var(--gr-gold-dark)',
                      color: 'var(--gr-navy)',
                      fontFamily: '"Oswald", sans-serif',
                      fontWeight: 600,
                      fontSize: '12.5px',
                      letterSpacing: '0.04em',
                      padding: '4px 12px',
                      borderRadius: '6px',
                      whiteSpace: 'nowrap',
                    }}
                    data-testid={`patent-num-${num.replace('/', '-').replace(',', '')}`}
                  >
                    <span style={{ color: 'var(--gr-gold-dark)', fontWeight: 700, marginRight: 4 }}>
                      U.S. Prov. App. No.
                    </span>
                    {num}
                  </span>
                ))}
              </div>
              <span className="text-[11px] text-gray-500 hidden md:inline">
                Filed 06/06/2026, 06/18/2026 &amp; 07/18/2026 &middot; USPTO Patent Center
              </span>
            </div>

            {/* This-week scrolling announcement (replaces the old reserved slot) */}
            <div
              className="hidden lg:flex items-center pl-4 overflow-hidden flex-1 max-w-[480px]"
              style={{ borderLeft: '1px dashed var(--gr-border)' }}
              data-testid="navbar-week-marquee"
            >
              <span
                className="text-[10px] uppercase tracking-widest font-bold mr-3 shrink-0"
                style={{ color: 'var(--gr-gold-dark)' }}
              >
                This Week
              </span>
              <div className="ai-marquee-track">
                <span className="ai-marquee-content">
                  This week actions to accomplish &nbsp;·&nbsp; This week actions to accomplish &nbsp;·&nbsp; This week actions to accomplish &nbsp;·&nbsp;
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
